import { Box, Btn, ScrollX, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { PickupBadge, StatusBadge } from './StatusBadge';
import { dateTime, money, num } from '../utils/format';
import type { OrderRecord, StatusDefinition } from '../api/types';

/**
 * Orders table.
 *
 * Columns are the fields an operations user actually works from. Internal
 * bookkeeping columns (created_by/updated_by, raw NDR JSON, credentials) are
 * never selected server-side, so they cannot leak here.
 *
 * The table scrolls horizontally inside its own container on narrow screens —
 * the page body never scrolls sideways. The pending-pickup column is pinned
 * second so the most operationally urgent signal survives that scroll.
 */
export type SortField = 'order_date' | 'order_no' | 'customer_name' | 'order_status' | 'order_total' | 'updated_at';

interface Column {
  key: string;
  header: string;
  sortField?: SortField;
  align?: 'left' | 'right';
  className?: string;
  render: (order: OrderRecord, catalog: StatusDefinition[]) => JSX.Element;
}

const COLUMNS: Column[] = [
  {
    key: 'order_no',
    header: 'Order ID',
    sortField: 'order_no',
    render: (o) => (
      <Txt className="text-[13px] font-medium text-ink-primary tabular whitespace-nowrap">{o.orderNo}</Txt>
    ),
  },
  {
    key: 'pickup',
    header: 'Pickup',
    render: (o, catalog) => <PickupBadge status={o.orderStatus} catalog={catalog} />,
  },
  {
    key: 'status',
    header: 'Status',
    sortField: 'order_status',
    render: (o, catalog) => <StatusBadge status={o.orderStatus} catalog={catalog} />,
  },
  {
    key: 'customer',
    header: 'Customer',
    sortField: 'customer_name',
    render: (o) => (
      <Box className="min-w-0">
        <Txt as="p" className="text-[13px] text-ink-primary truncate max-w-[220px]">
          {o.customerName ?? '—'}
        </Txt>
        {o.customerCity ? (
          <Txt as="p" className="text-[11px] text-ink-muted truncate max-w-[220px]">
            {o.customerCity}
            {o.pincode ? ` · ${o.pincode}` : ''}
          </Txt>
        ) : null}
      </Box>
    ),
  },
  {
    key: 'order_date',
    header: 'Order Date',
    sortField: 'order_date',
    render: (o) => <Txt className="text-[13px] text-ink-secondary tabular whitespace-nowrap">{dateTime(o.orderDate)}</Txt>,
  },
  {
    key: 'courier',
    header: 'Courier / AWB',
    render: (o) => (
      <Box className="min-w-0">
        <Txt as="p" className="text-[13px] text-ink-secondary capitalize whitespace-nowrap">
          {o.courierSlug ? o.courierSlug.replace(/_/g, ' ') : '—'}
        </Txt>
        {o.awb ? (
          <Txt as="p" className="text-[11px] text-ink-muted tabular whitespace-nowrap">
            {o.awb}
          </Txt>
        ) : null}
      </Box>
    ),
  },
  {
    key: 'items',
    header: 'Items',
    align: 'right',
    render: (o) => <Txt className="text-[13px] text-ink-secondary tabular">{num(o.itemCount)}</Txt>,
  },
  {
    key: 'total',
    header: 'Order Total',
    sortField: 'order_total',
    align: 'right',
    render: (o) => (
      <Box>
        <Txt as="p" className="text-[13px] text-ink-primary tabular whitespace-nowrap">
          {money(o.orderTotal)}
        </Txt>
        <Txt as="p" className="text-[11px] text-ink-muted whitespace-nowrap">
          {o.paymentType}
        </Txt>
      </Box>
    ),
  },
  {
    key: 'updated_at',
    header: 'Updated',
    sortField: 'updated_at',
    render: (o) => <Txt className="text-[13px] text-ink-secondary tabular whitespace-nowrap">{dateTime(o.updatedAt)}</Txt>,
  },
];

export interface OrdersTableProps {
  orders: OrderRecord[];
  catalog: StatusDefinition[];
  sortBy: SortField;
  sortDir: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  pendingStatuses: readonly string[];
}

export function OrdersTable({
  orders,
  catalog,
  sortBy,
  sortDir,
  onSort,
  pendingStatuses,
}: OrdersTableProps): JSX.Element {
  return (
    <ScrollX>
      <Box as="div" role="table" className="min-w-[980px]">
        {/* Header */}
        <Box role="row" className="flex items-center gap-4 px-5 py-2.5 bg-surface-sunken border-y border-hairline">
          {COLUMNS.map((col) => {
            const active = col.sortField === sortBy;
            const content = (
              <Box className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                <Txt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted whitespace-nowrap">
                  {col.header}
                </Txt>
                {col.sortField ? (
                  <Box
                    className="transition-transform duration-150"
                    style={{
                      opacity: active ? 1 : 0.28,
                      transform: active && sortDir === 'asc' ? 'rotate(180deg)' : 'none',
                    }}
                  >
                    <Icon name="chevron-down" className="w-3 h-3" strokeWidth={2.5} />
                  </Box>
                ) : null}
              </Box>
            );

            return (
              <Box key={col.key} role="columnheader" className={colWidth(col.key)}>
                {col.sortField ? (
                  <Btn
                    onPress={() => onSort(col.sortField as SortField)}
                    aria-label={`Sort by ${col.header}`}
                    className="w-full rounded hover:opacity-80 transition-opacity duration-150"
                  >
                    {content}
                  </Btn>
                ) : (
                  content
                )}
              </Box>
            );
          })}
        </Box>

        {/* Rows */}
        {orders.map((order) => {
          const isPending = pendingStatuses.includes(order.orderStatus);
          return (
            <Box
              key={order.id}
              role="row"
              className="relative flex items-center gap-4 px-5 py-3 border-b border-hairline last:border-0 transition-colors duration-150 hover:bg-surface-sunken"
            >
              {/* Pending orders get a quiet accent rail: the table stays scannable
                  for "what still needs collecting" without shouting. */}
              {isPending ? (
                <Box
                  className="absolute left-0 top-0 bottom-0 w-[3px]"
                  style={{ backgroundColor: 'var(--life-pending)' }}
                  aria-hidden
                />
              ) : null}
              {COLUMNS.map((col) => (
                <Box
                  key={col.key}
                  role="cell"
                  className={`${colWidth(col.key)} ${col.align === 'right' ? 'text-right' : ''}`}
                >
                  {col.render(order, catalog)}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </ScrollX>
  );
}

/** Fixed column widths keep the header and body aligned inside the scroller. */
function colWidth(key: string): string {
  switch (key) {
    case 'order_no':
      return 'w-[130px] shrink-0';
    case 'pickup':
      return 'w-[125px] shrink-0';
    case 'status':
      return 'w-[130px] shrink-0';
    case 'customer':
      return 'flex-1 min-w-[180px]';
    case 'order_date':
      return 'w-[125px] shrink-0';
    case 'courier':
      return 'w-[130px] shrink-0';
    case 'items':
      return 'w-[55px] shrink-0';
    case 'total':
      return 'w-[110px] shrink-0';
    case 'updated_at':
      return 'w-[125px] shrink-0';
    default:
      return 'flex-1';
  }
}
